import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { API } from "@/App";
import api from "@/api/axios";
import { toast } from "react-hot-toast";
import { DayPicker } from "react-day-picker";
import "react-day-picker/style.css";


const formatDate = (date) => date.toISOString().split("T")[0];

const getPreviewText = ({ mode, from, to, selectedDates }) => {
  if (mode === "range" && from && to) return `Date Range: ${from.toLocaleDateString()} - ${to.toLocaleDateString()}`;
  if (mode === "single" && from) return `Date Selected: ${from.toLocaleDateString()}`;
  if (mode === "multiple" && selectedDates?.length === 1) return `Date Selected: ${selectedDates[0].toLocaleDateString()}`;
  if (mode === "multiple" && selectedDates?.length > 1) return `Selected Dates: ${selectedDates.map(d => d.toLocaleDateString()).join(", ")}`;
  return "";
};


const FlexibleDatePicker = ({ form, setForm, label }) => {
  const [mode, setMode] = useState("single"); 
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [selectedDates, setSelectedDates] = useState([]);

  const handleSelect = (value) => {
    if (mode === "range") {
      setRange(value);
      if (value.from && value.to) {
        const dates = [];
        let current = new Date(value.from);
        const end = new Date(value.to);
        while (current <= end) {
          dates.push(formatDate(current));
          current.setDate(current.getDate() + 1);
        }
        setForm({ ...form, dates });
      }
    } else if (mode === "single") {
      setRange({ from: value, to: value });
      if (value) setForm({ ...form, dates: [formatDate(value)] });
    } else if (mode === "multiple") {
      setSelectedDates(value);
      setForm({ ...form, dates: value.map(formatDate) });
    }
  };

  const dayPickerMode = mode === "range" ? "range" : mode === "multiple" ? "multiple" : "single";

  return (
    <div className="sm:col-span-2">
      <Label>{label}</Label>
      <Select value={mode} onValueChange={setMode}>
        <SelectTrigger>
          <SelectValue placeholder="Select mode" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="single">Single Date</SelectItem>
          <SelectItem value="range">Date Range</SelectItem>
          <SelectItem value="multiple">Multiple Dates</SelectItem>
        </SelectContent>
      </Select>

      <DayPicker
        mode={dayPickerMode}
        selected={mode === "multiple" ? selectedDates : range}
        onSelect={handleSelect}
        numberOfMonths={1}
      />

      {(range.from || range.to || selectedDates.length > 0) && (
        <div className="mt-2 text-sm text-slate-700 dark:text-slate-300">
          {getPreviewText({ mode, from: range.from, to: range.to, selectedDates })}
        </div>
      )}
    </div>
  );
};

// --- Holiday Form ---
const HolidayForm = ({ form, setForm }) => (
  <>
    <div className="sm:col-span-2">
      <Label htmlFor="holiday-name">Name</Label>
      <Input
        id="holiday-name"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        required
      />
    </div>

    <FlexibleDatePicker form={form} setForm={setForm} label="Select Date(s)" />

    <div className="flex items-center space-x-2 mt-4">
      <Switch
        id="is-paid"
        checked={form.is_paid}
        onCheckedChange={(checked) => setForm({ ...form, is_paid: checked })}
      />
      <Label htmlFor="is-paid">Paid Holiday</Label>
    </div>
  </>
);

// --- Event Form ---
const EventForm = ({ form, setForm, googleStatus, teamUsers }) => (
    <>
      <div className="sm:col-span-2">
        <Label htmlFor="event-title">Title</Label>
        <Input
          id="event-title"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          required
        />
      </div>

      <FlexibleDatePicker form={form} setForm={setForm} label="Select Date(s)" />

      <div>
        <Label>Type</Label>
        <Select
          value={form.type}
          onValueChange={(v) => setForm({ ...form, type: v, create_meet: v === "Meeting" })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select an event type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Event">Event</SelectItem>
            <SelectItem value="Meeting">Meeting</SelectItem>
            <SelectItem value="Site Visit">Site Visit</SelectItem>
          </SelectContent>
        </Select>
      </div>
  
      {/* Show time and notes only for meetings */}
      {form.type === "Meeting" && (
        <>
          <div className="sm:col-span-2 grid grid-cols-2 gap-4 mt-2">
            <div>
              <Label htmlFor="start-time">Start Time</Label>
              <Input
                id="start-time"
                type="time"
                value={form.start_time || ""}
                onChange={(e) => setForm({ ...form, start_time: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="end-time">End Time</Label>
              <Input
                id="end-time"
                type="time"
                value={form.end_time || ""}
                onChange={(e) => setForm({ ...form, end_time: e.target.value })}
              />
            </div>
          </div>
  
          <div className="sm:col-span-2 mt-2">
            <Label htmlFor="notes">Notes</Label>
            <textarea
              id="notes"
              value={form.notes || ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full rounded border border-slate-300 p-2"
              rows={3}
              placeholder="Additional details for the meeting"
            />
          </div>
        </>
      )}

      {/* Google Calendar sync — only once we know the workspace status */}
      {googleStatus && googleStatus.configured && googleStatus.connected && (
        <div className="sm:col-span-2 mt-2 border-t border-slate-200 dark:border-slate-700 pt-4">
          <div className="flex items-center space-x-2">
            <Switch
              id="sync-to-google"
              checked={!!form.sync_to_google}
              onCheckedChange={(checked) => setForm({ ...form, sync_to_google: checked })}
            />
            <Label htmlFor="sync-to-google">Add to Google Calendar</Label>
          </div>

          {form.sync_to_google && (
            <>
              <div className="flex items-center space-x-2 mt-3">
                <Switch
                  id="create-meet"
                  checked={!!form.create_meet}
                  onCheckedChange={(checked) => setForm({ ...form, create_meet: checked })}
                />
                <Label htmlFor="create-meet">Create Google Meet link</Label>
              </div>

              <div className="mt-3">
                <Label>Invite attendees</Label>
                <div className="mt-1 border border-slate-300 dark:border-slate-700 rounded max-h-40 overflow-y-auto p-2">
                  {teamUsers.length === 0 ? (
                    <div className="text-sm text-slate-500 dark:text-slate-400">
                      No colleagues available.
                    </div>
                  ) : (
                    teamUsers.map((u) => (
                      <label
                        key={u.id || u.email}
                        htmlFor={`attendee-${u.id || u.email}`}
                        className="flex items-center gap-2 py-1 text-sm cursor-pointer"
                      >
                        <input
                          id={`attendee-${u.id || u.email}`}
                          type="checkbox"
                          checked={(form.attendees || []).includes(u.email)}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              attendees: e.target.checked
                                ? [...(form.attendees || []), u.email]
                                : (form.attendees || []).filter((a) => a !== u.email),
                            })
                          }
                        />
                        <span>
                          {u.name} <span className="text-slate-500 dark:text-slate-400">({u.email})</span>
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {googleStatus && googleStatus.configured && !googleStatus.connected && (
        <div className="sm:col-span-2 mt-2 text-sm text-slate-500 dark:text-slate-400">
          Connect your Google account on the Calendar page to create Meet links.
        </div>
      )}
    </>
  );
  

const buildInitialForm = (isHoliday) =>
  isHoliday
    ? { name: "", dates: [], is_paid: true }
    : { title: "", dates: [], type: "Event", sync_to_google: false, create_meet: false, attendees: [] };

// --- Dialog Component ---
export const HolidaysAndEventsDialog = ({ isDialogOpen, setIsDialogOpen, entryType }) => {
  const isHoliday = entryType === "Holiday";

  const [form, setForm] = useState(() => buildInitialForm(isHoliday));
  // Bumped on every reset so the sub-form (and the DayPicker's internal selection) remounts.
  const [formKey, setFormKey] = useState(0);
  // null while unknown -> the Google sub-section renders nothing
  const [googleStatus, setGoogleStatus] = useState(null);
  const [teamUsers, setTeamUsers] = useState([]);
  const [usersRequested, setUsersRequested] = useState(false);

  const resetForm = () => {
    setForm(buildInitialForm(isHoliday));
    setFormKey((k) => k + 1);
  };

  // The dialog stays mounted while `entryType` toggles between Holiday and Event,
  // so the form has to be rebuilt into the shape the visible sub-form expects.
  useEffect(() => {
    setForm(buildInitialForm(isHoliday));
    setFormKey((k) => k + 1);
  }, [isHoliday]);

  useEffect(() => {
    if (isHoliday || !isDialogOpen) return;
    let cancelled = false;
    api
      .get(`${API}/google/status`, { silent: true })
      .then((r) => {
        if (!cancelled)
          setGoogleStatus({ configured: !!r.data?.configured, connected: !!r.data?.connected });
      })
      .catch(() => {
        if (!cancelled) setGoogleStatus({ configured: false, connected: false });
      });
    return () => {
      cancelled = true;
    };
  }, [isHoliday, isDialogOpen]);

  useEffect(() => {
    if (usersRequested) return;
    if (!googleStatus?.connected || !form.sync_to_google) return;
    setUsersRequested(true);
    api
      .get(`${API}/users`, { silent: true })
      .then((r) =>
        setTeamUsers((Array.isArray(r.data) ? r.data : []).filter((u) => u?.email && u.is_active !== false))
      )
      .catch(() => setTeamUsers([]));
  }, [usersRequested, googleStatus, form.sync_to_google]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = isHoliday
        ? form
        : {
            ...form,
            sync_to_google: !!form.sync_to_google,
            create_meet: !!form.sync_to_google && !!form.create_meet,
            attendees: form.sync_to_google ? form.attendees || [] : [],
          };
      const res = await api.post(`${API}/${isHoliday ? "holidays" : "events"}`, payload);
      if (!isHoliday) {
        if (res.data?.google_meet_link) {
          toast.success(`Google Meet link created: ${res.data.google_meet_link}`);
        } else if (res.data?.google_sync_error) {
          toast.error(`${entryType} saved, but Google Calendar sync failed: ${res.data.google_sync_error}`);
        }
      }
      resetForm();
      setIsDialogOpen(false);
    } catch (ex) {
      alert(ex.response?.data?.detail || `Failed to submit ${entryType}`);
    }
  };

  const handleOpenChange = (open) => {
    if (!open) resetForm();
    setIsDialogOpen(open);
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle>Add New {entryType}</DialogTitle>
          <DialogDescription>
            Fill in the details for the new {entryType.toLowerCase()}.
          </DialogDescription>
        </DialogHeader>

        <form id="entry-form" onSubmit={handleSubmit} className="grid gap-4 py-4 sm:grid-cols-2">
          {isHoliday ? (
            <HolidayForm key={formKey} form={form} setForm={setForm} />
          ) : (
            <EventForm
              key={formKey}
              form={form}
              setForm={setForm}
              googleStatus={googleStatus}
              teamUsers={teamUsers}
            />
          )}
        </form>

        <div className="sticky bottom-0 bg-white dark:bg-slate-900 py-4 flex justify-end gap-2 border-t border-slate-200 dark:border-slate-700">
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form="entry-form">
            Submit
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
